
// --- Shared session/token helpers (Cognito is the single source of truth) ---
import {
  CognitoUserPool,
  CognitoUser,
} from 'amazon-cognito-identity-js';
import { cognitoSignInWithPassword } from './cognito/passwordAuth';
import { respondToMfaChallenge } from './cognito/respondToChallenge';
import { setActiveChallengeState } from './cognito/challengeState';
import {
  getFromLocalStorage,
  LOCAL_STORAGE_ITEM,
  removeFromLocalStorage,
  setToLocalStorage,
} from '@utils/localStorageHelper';
import { errors } from '@services/errorHandlingService/errors';
import { isCognitoPasswordResetRequiredError } from '@services/cognitoErrorNormalizer';
import { isSharedVideoChatV2MeetingActive } from '@services/meetingPresenceService';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
};
const userPool = new CognitoUserPool(poolData);

const sanitizeUserAttributes = (userAttributes) => {
  const sanitized = { ...(userAttributes || {}) };
  delete sanitized.sub;
  delete sanitized.email_verified;
  delete sanitized.phone_number_verified;
  return sanitized;
};

const buildRequiredAttributesPayload = (sanitizedAttributes, requiredAttributes) => {
  // IMPORTANT: For NEW_PASSWORD_REQUIRED you should send ONLY the missing/required attributes.
  // Sending attributes like `email` back can be treated as an attempt to modify them.
  const payload = {};
  if (Array.isArray(requiredAttributes) && requiredAttributes.length) {
    requiredAttributes.forEach((attrName) => {
      if (sanitizedAttributes[attrName] !== undefined) {
        payload[attrName] = sanitizedAttributes[attrName];
      }
    });
  }
  return payload;
};

export function cognitoSignUp({ email, password }) {
  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, [{ Name: 'email', Value: email }], null, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
}

// Verify email address by verification code
export function verifyEmailWithCode(username, code) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  return new Promise((resolve) => {
    user.confirmRegistration(code, true, function (err, result) {
      if (err) {
        resolve({ success: false, error: err });
      } else {
        resolve({ success: true, result });
      }
    });
  });
}

// Resend verification email
export function resendVerificationEmail(username) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  return new Promise((resolve) => {
    user.resendConfirmationCode(function (err, result) {
      if (err) {
        resolve({ success: false, error: err });
      } else {
        resolve({ success: true, result });
      }
    });
  });
}

// Sign in with Cognito and return user attributes
export function cognitoSignIn(email, password) {
  return cognitoSignInWithPassword({ userPool, email, password });
}

export function cognitoCompleteNewPasswordChallenge({ email, session, newPassword, userAttributes, requiredAttributes }) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  // The SDK expects the challenge Session to be present on the CognitoUser instance.
  if (session) {
    user.Session = session;
  }

  const sanitizedAttributes = sanitizeUserAttributes(userAttributes);
  const attributesToSend = buildRequiredAttributesPayload(
    sanitizedAttributes,
    requiredAttributes,
  );

  return new Promise((resolve, reject) => {
    const challengeResponses = {
      USERNAME: email,
      NEW_PASSWORD: newPassword,
      ...attributesToSend,
    };

    try {
      user.client.request('RespondToAuthChallenge', {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: userPool.getClientId(),
        Session: user.Session,
        ChallengeResponses: challengeResponses,
      }, (err, data) => {
        if (err) {
          reject(err);
          return;
        }

        if (data?.AuthenticationResult) {
          try {
            const cognitoSub = data.AuthenticationResult?.IdToken
              ? JSON.parse(atob(data.AuthenticationResult.IdToken.split('.')[1]))?.sub || null
              : null;
            const cognitoToken = data.AuthenticationResult?.AccessToken || null;
            user.signOut();
            resolve({
              success: true,
              payload: { cognitoSub, token: cognitoToken },
            });
          } catch (_) {
            try {
              user.signOut();
            } catch (__){
              // ignore
            }
            resolve({
              success: true,
              payload: { cognitoSub: null, token: data.AuthenticationResult?.AccessToken || null },
            });
          }
          return;
        }

        if (data?.ChallengeName) {
          const nextSession = data?.Session || null;
          const challengeParameters = data?.ChallengeParameters || {};
          const challengeUsername = challengeParameters?.USER_ID_FOR_SRP || email;

          user.Session = nextSession;
          setActiveChallengeState({
            user,
            username: challengeUsername,
            challengeName: data.ChallengeName,
            session: nextSession,
          });

          resolve({
            success: false,
            challenge: data.ChallengeName,
            payload: {
              email: challengeUsername,
              session: nextSession,
              challengeParameters,
            },
          });
          return;
        }

        reject(new Error('Unknown NEW_PASSWORD_REQUIRED response'));
      });
    } catch (err) {
      reject(err);
    }
  });
}

// Forgot password: send a verification code to the user's email
export function cognitoForgotPassword(email) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve) => {
    user.forgotPassword({
      onSuccess: (result) => {
        resolve({ success: true, result });
      },
      onFailure: (err) => {
        resolve({ success: false, error: err });
      },
      inputVerificationCode: (data) => {
        // Cognito has sent a code to the user; UI will ask for it.
        resolve({
          success: true,
          result: {
            codeSent: true,
            codeDeliveryDetails: data?.CodeDeliveryDetails || null,
          },
        });
      },
    });
  });
}

// Change password for currently signed-in user (requires current/old password)
export function cognitoChangePassword({ oldPassword, newPassword }) {
  const currentUser = userPool.getCurrentUser();
  return new Promise((resolve) => {
    if (!currentUser) {
      resolve({
        success: false,
        error: {
          name: 'NotAuthorizedException',
          message: 'Not signed in',
        },
      });
      return;
    }

    currentUser.getSession((sessionErr, session) => {
      if (sessionErr || !session || !session.isValid()) {
        resolve({
          success: false,
          error: sessionErr || {
            name: 'NotAuthorizedException',
            message: 'Session is not valid',
          },
        });
        return;
      }

      currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          resolve({ success: false, error: err });
          return;
        }
        resolve({ success: true, result });
      });
    });
  });
}

// Sign out from Cognito
export const cognitoLogOut = async ({ timeoutMs = 3000 } = {}) => {
  const withTimeout = (promise, ms) => {
    if (!ms || ms <= 0) return promise;

    return new Promise((resolve) => {
      let settled = false;

      const t = setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve({ ok: false, timeout: true });
      }, ms);

      promise
        .then((value) => {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          resolve(value);
        })
        .catch(() => {
          if (settled) return;
          settled = true;
          clearTimeout(t);
          resolve({ ok: false, error: true });
        });
    });
  };

  const getSessionBestEffort = (currentUser) => {
    if (!currentUser || typeof currentUser.getSession !== 'function') {
      return Promise.resolve({ ok: false, skipped: true });
    }

    return new Promise((resolve) => {
      try {
        currentUser.getSession((err, session) => {
          if (err || !session) {
            resolve({ ok: false, err: err || null, session: null });
            return;
          }
          resolve({ ok: true, session });
        });
      } catch (err) {
        resolve({ ok: false, err, session: null });
      }
    });
  };

  const globalSignOutBestEffort = (currentUser) => {
    if (!currentUser || typeof currentUser.globalSignOut !== 'function') {
      return Promise.resolve({ ok: true, skipped: true });
    }

    return new Promise((resolve) => {
      try {
        currentUser.globalSignOut({
          onSuccess: () => resolve({ ok: true }),
          onFailure: (err) => resolve({ ok: false, err: err || null }),
        });
      } catch (_) {
        resolve({ ok: false, error: true });
      }
    });
  };

  try {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return;

    const startMs = Date.now();
    const remainingMs = () => Math.max(0, timeoutMs - (Date.now() - startMs));

    // Best-effort: attempt server-side global sign-out, but never block logout forever.
    // Some SDK versions/conditions short-circuit with "User is not authenticated" if the
    // session isn't loaded yet; in that case, try to load/refresh the session and retry.
    const firstAttempt = await withTimeout(
      globalSignOutBestEffort(currentUser),
      remainingMs(),
    );

    const errMessage = String(firstAttempt?.err?.message || '').toLowerCase();
    const shouldRetry = !firstAttempt?.ok && errMessage.includes('not authenticated');

    if (shouldRetry && remainingMs() > 0) {
      await withTimeout(getSessionBestEffort(currentUser), remainingMs());
      await withTimeout(globalSignOutBestEffort(currentUser), remainingMs());
    }

    if (process.env.NODE_ENV !== 'production') {
      // Breadcrumb for debugging; do not log tokens or user identifiers.
      // eslint-disable-next-line no-console
      console.debug('[cognito] logout: globalSignOut attempted', {
        ok: Boolean(firstAttempt?.ok),
        timedOut: Boolean(firstAttempt?.timeout),
        retried: Boolean(shouldRetry),
      });
    }

    // Always clear local session.
    currentUser.signOut();
  } catch (_) {
    // Fail-open: local logout is handled elsewhere; never throw from logout.
  }
};

export const getCognitoAccessToken = () => {
  return Promise.resolve(getFromLocalStorage(LOCAL_STORAGE_ITEM.TOKEN));
};

export const forceRefreshCognitoSession = () => {
  return new Promise((resolve, reject) => {
    const lastUser = userPool.getCurrentUser();
    if (!lastUser) {
      resolve(getFromLocalStorage(LOCAL_STORAGE_ITEM.TOKEN));
      return;
    }
    lastUser.getSession((err, session) => {
      if (err && isCognitoPasswordResetRequiredError(err)) {
        // Avoid interrupting an active meeting; defer token clearing until user exits meeting flow.
        if (!isSharedVideoChatV2MeetingActive()) {
          // Ensure we stop attaching a stale/invalid token to requests.
          removeFromLocalStorage(LOCAL_STORAGE_ITEM.TOKEN);
        }
        reject(new Error(errors.PASSWORD_RESET_REQUIRED));
        return;
      }

      if (err || !session || !session.isValid()) {
        resolve(getFromLocalStorage(LOCAL_STORAGE_ITEM.TOKEN));
      } else {
        const token = session.getAccessToken().getJwtToken();
        setToLocalStorage(LOCAL_STORAGE_ITEM.TOKEN, token);
        resolve(token);
      }
    });
  });
};

// Handles MFA challenge response
export function cognitoRespondToMFAChallenge({ username, code, session, challengeName }) {
  return respondToMfaChallenge({ userPool, username, code, session, challengeName });
}
// --- Shared session/token helpers (Cognito is the single source of truth) ---
